/**
 * PRP-WEB-2 W2.7: the underlay's controls (a small bar over the plan's bottom-right) and its
 * SVG image. Pick a picture, then "Set scale" (draw a line over a wall you know, type its
 * length), "Move picture" (drag it to line up), the opacity slider, and Remove.
 */
import { useRef } from 'react';
import { ImagePlus, Move, Ruler, Trash2 } from 'lucide-react';
import type { UnderlayState } from './underlay';

export type UnderlayMode = 'none' | 'calibrate' | 'move';

export function UnderlayImage({ u }: { u: UnderlayState }) {
  if (!u.natW || !u.natH) return null;
  const w = u.natW * u.scale_ft_per_px, h = u.natH * u.scale_ft_per_px;
  return (
    <image data-testid="plan-underlay" href={u.url} x={u.offset.x} y={u.offset.y} width={w} height={h} opacity={u.opacity}
      preserveAspectRatio="none" transform={u.rotation ? `rotate(${u.rotation} ${u.offset.x} ${u.offset.y})` : undefined} />
  );
}

export interface UnderlayControlsProps {
  u: UnderlayState | null;
  mode: UnderlayMode;
  busy: boolean;
  error: string | null;
  onPick: (f: File) => void;
  onMode: (m: UnderlayMode) => void;
  onOpacity: (v: number) => void;
  onRemove: () => void;
}

const btn = 'inline-flex h-8 items-center gap-1.5 rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-3)] px-2 text-[12px] font-[560] text-[var(--tx-hi)] hover:bg-[var(--bg-4)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-line)] disabled:opacity-50 [&_svg]:h-3.5 [&_svg]:w-3.5';

export function UnderlayControls({ u, mode, busy, error, onPick, onMode, onOpacity, onRemove }: UnderlayControlsProps) {
  const file = useRef<HTMLInputElement>(null);
  return (
    <div data-testid="underlay-controls" onPointerDown={(e) => e.stopPropagation()}
      className="absolute bottom-3 right-3 z-10 flex max-w-[calc(100%-24px)] flex-wrap items-center justify-end gap-1.5 rounded-[var(--r-md)] border border-[var(--bd)] bg-[var(--bg-2)]/95 p-1.5 shadow-[var(--sh-sm)]">
      <input ref={file} type="file" accept="image/*" className="hidden" data-testid="underlay-file"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = ''; }} />
      <button type="button" className={btn} disabled={busy} onClick={() => file.current?.click()} data-testid="underlay-pick">
        <ImagePlus />{u ? 'Change picture' : 'Trace over a picture'}
      </button>
      {u && (
        <>
          <button type="button" className={`${btn} ${mode === 'calibrate' ? 'ring-2 ring-[var(--accent)]' : ''}`} aria-pressed={mode === 'calibrate'}
            data-testid="underlay-calibrate" onClick={() => onMode(mode === 'calibrate' ? 'none' : 'calibrate')}><Ruler />Set scale</button>
          <button type="button" className={`${btn} ${mode === 'move' ? 'ring-2 ring-[var(--accent)]' : ''}`} aria-pressed={mode === 'move'}
            data-testid="underlay-move" onClick={() => onMode(mode === 'move' ? 'none' : 'move')}><Move />Move picture</button>
          <label className="flex items-center gap-1 text-[11.5px] text-[var(--tx-lo)]">
            See-through
            <input type="range" min={10} max={100} value={Math.round(u.opacity * 100)} aria-label="Picture opacity" data-testid="underlay-opacity"
              onChange={(e) => onOpacity(Number(e.target.value) / 100)} className="w-20 accent-[var(--accent)]" />
          </label>
          <button type="button" className={btn} aria-label="Remove the picture" title="Remove the picture" onClick={onRemove}><Trash2 /></button>
        </>
      )}
      {mode === 'calibrate' && <span className="w-full text-right text-[11.5px] text-[var(--tx-mid)]">Drag along a wall whose length you know.</span>}
      {mode === 'move' && <span className="w-full text-right text-[11.5px] text-[var(--tx-mid)]">Drag to line the picture up with the plan.</span>}
      {error && <span role="alert" className="w-full text-right text-[11.5px] text-[var(--bad)]">{error}</span>}
    </div>
  );
}
